/**
 * Broker Connect — Kotak Neo session management.
 *
 * Live adapters are held in the API process, so a backend restart invalidates
 * a session and it has to be re-established here.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Copy, Globe, Plug, RefreshCw, ShieldCheck, Unplug } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { formatDateTime } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
} from '@/components/ui';

const brokerConnectSchema = z.object({
  consumer_key: z.string().min(1, 'Consumer key is required'),
  mobile_number: z.string().regex(/^\+?[0-9]{10,13}$/, 'Enter a valid mobile number'),
  ucc: z.string().min(1, 'UCC is required'),
  totp: z
    .string()
    .min(6, 'TOTP must be a 6-digit code or secret key')
    .regex(/^[A-Za-z0-9\s-]+$/, 'TOTP must be a numeric code or base32 secret key'),
  mpin: z.string().min(4, 'MPIN must be 4-6 digits').max(6).regex(/^[0-9]+$/, 'MPIN must be numeric'),
  display_name: z.string().max(128).optional(),
});

type BrokerConnectFormData = z.infer<typeof brokerConnectSchema>;

interface BrokerStatus {
  connection_id: string;
  broker_type: string;
  display_name: string;
  ucc: string;
  is_active: boolean;
  is_session_valid: boolean;
  session_expires_at: string | null;
  last_connected_at: string | null;
}

/** Public IPv4 of the API process — what Kotak's Trade API whitelist needs (orders are refused otherwise). */
interface EgressIp {
  ipv4: string | null;
  message: string;
}

export default function BrokerConnect() {
  const [connections, setConnections] = useState<BrokerStatus[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [egress, setEgress] = useState<EgressIp | null>(null);
  const [egressLoading, setEgressLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BrokerConnectFormData>({
    resolver: zodResolver(brokerConnectSchema),
  });

  const fetchConnections = async () => {
    try {
      const { data } = await api.get('/brokers/status');
      setConnections(data);
    } catch {
      /* non-critical — an empty list is a valid state */
    }
  };

  const fetchEgressIp = async (refresh = false) => {
    setEgressLoading(true);
    try {
      const { data } = await api.get('/brokers/egress-ip', { params: refresh ? { refresh: true } : undefined });
      setEgress(data);
    } catch (err) {
      setEgress({ ipv4: null, message: getErrorMessage(err) });
    } finally {
      setEgressLoading(false);
    }
  };

  const copyEgressIp = async () => {
    if (!egress?.ipv4) return;
    try {
      await navigator.clipboard.writeText(egress.ipv4);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — the address is still visible */
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchEgressIp();
  }, []);

  const onConnect = async (data: BrokerConnectFormData) => {
    setError('');
    setSuccess('');
    setIsConnecting(true);
    try {
      const { data: result } = await api.post('/brokers/connect', {
        broker_type: 'kotak_neo',
        ...data,
      });
      setSuccess(result.message || 'Connected to broker successfully');
      setShowForm(false);
      reset();
      await fetchConnections();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsConnecting(false);
    }
  };

  const onDisconnect = async (connectionId: string) => {
    setError('');
    setIsDisconnecting(connectionId);
    try {
      await api.post('/brokers/disconnect', { connection_id: connectionId });
      setSuccess('Disconnected from broker');
      await fetchConnections();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsDisconnecting(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert tone="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert tone="success" onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Kotak Neo"
          description="The only broker adapter currently implemented"
          actions={
            <Button variant="primary" size="sm" icon={Plug} onClick={() => setShowForm(true)} id="add-broker-btn">
              Connect broker
            </Button>
          }
        />

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface-2 px-5 py-3 text-xs text-fg-subtle">
          <Globe className="h-3.5 w-3.5 shrink-0 text-fg-faint" aria-hidden="true" />
          <span className="font-medium text-fg">Server IP for Kotak's Trade API whitelist</span>
          <span className="font-tabular text-sm text-fg" data-testid="egress-ip">
            {egressLoading && !egress ? 'Checking…' : (egress?.ipv4 ?? 'unknown')}
          </span>
          {egress?.ipv4 && (
            <Button variant="ghost" size="sm" icon={Copy} onClick={copyEgressIp} aria-label="Copy server IP">
              {copied ? 'Copied' : 'Copy'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={RefreshCw}
            loading={egressLoading}
            onClick={() => fetchEgressIp(true)}
            aria-label="Re-check server IP"
          >
            Re-check
          </Button>
          <span className="basis-full text-fg-subtle">
            {egress?.ipv4
              ? 'Kotak accepts place/modify/cancel only from a whitelisted static IPv4 whose session was created from it: Kotak Neo → More → Trade API → Add IP, then reconnect the broker here. A different address answers "unauthorized" (stCode 100008).'
              : egress?.message || 'Kotak accepts orders only from a whitelisted static IPv4 (Kotak Neo → More → Trade API → Add IP).'}
          </span>
        </div>

        {connections.length === 0 ? (
          <EmptyState
            icon={Plug}
            title="No broker connected"
            description="Connect your Kotak Neo account to place orders and stream live market data."
            action={
              <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
                Connect now
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {connections.map((conn) => (
              <li key={conn.connection_id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-fg">{conn.display_name || 'Kotak Neo'}</p>
                    <Badge
                      tone={conn.is_session_valid ? 'success' : 'warning'}
                      icon={conn.is_session_valid ? ShieldCheck : undefined}
                    >
                      {conn.is_session_valid ? 'Session valid' : 'Session expired'}
                    </Badge>
                    {!conn.is_active && <Badge tone="neutral">Inactive</Badge>}
                  </div>
                  <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-subtle">
                    <div className="flex gap-1.5">
                      <dt className="text-fg-faint">UCC</dt>
                      <dd className="font-tabular">{conn.ucc}</dd>
                    </div>
                    <div className="flex gap-1.5">
                      <dt className="text-fg-faint">Connected</dt>
                      <dd className="font-tabular">{formatDateTime(conn.last_connected_at)}</dd>
                    </div>
                    <div className="flex gap-1.5">
                      <dt className="text-fg-faint">Expires</dt>
                      <dd className="font-tabular">{formatDateTime(conn.session_expires_at)}</dd>
                    </div>
                  </dl>
                </div>

                <Button
                  variant="danger"
                  size="sm"
                  icon={Unplug}
                  loading={isDisconnecting === conn.connection_id}
                  onClick={() => onDisconnect(conn.connection_id)}
                >
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Connect Kotak Neo"
        description="Credentials are encrypted before storage and used only to establish a broker session."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit" form="broker-connect-form" variant="primary" loading={isConnecting} icon={Plug}>
              Connect
            </Button>
          </>
        }
      >
        <form id="broker-connect-form" onSubmit={handleSubmit(onConnect)} className="flex flex-col gap-4" noValidate>
          <Field label="Consumer key" error={errors.consumer_key?.message} required>
            {(field) => <Input {...field} mono autoComplete="off" {...register('consumer_key')} />}
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Mobile number" error={errors.mobile_number?.message} required>
              {(field) => <Input {...field} inputMode="tel" placeholder="+919876543210" {...register('mobile_number')} />}
            </Field>

            <Field label="UCC" error={errors.ucc?.message} required>
              {(field) => <Input {...field} mono {...register('ucc')} />}
            </Field>

            <Field
              label="TOTP"
              error={errors.totp?.message}
              hint="6-digit code, or the base32 seed for unattended re-login"
              required
            >
              {(field) => <Input {...field} mono autoComplete="one-time-code" {...register('totp')} />}
            </Field>

            <Field label="MPIN" error={errors.mpin?.message} required>
              {(field) => (
                <Input {...field} type="password" mono inputMode="numeric" autoComplete="off" {...register('mpin')} />
              )}
            </Field>
          </div>

          <Field label="Display name" error={errors.display_name?.message} hint="Optional label for this connection">
            {(field) => <Input {...field} placeholder="Primary account" {...register('display_name')} />}
          </Field>

          <p className="flex items-start gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-xs text-fg-subtle">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-faint" aria-hidden="true" />
            Sessions live in the API process — restarting the backend requires reconnecting here.
          </p>
        </form>
      </Modal>
    </div>
  );
}
